import apiClient from '@/services/api_client';

export type AnalyticsPeriod = '7d' | '1m' | '3m' | '6m' | '1y';

export type Metric = {
    value: number;
    /** Change against the previous window, or null when there is no baseline. */
    delta_pct: number | null;
};

export type LabeledCount = {
    label: string;
    count: number;
};

export type ModelUsageCount =
    | { model_id: string; model_label: string; count: number }
    | LabeledCount;

export type ToolFamilyUsage = {
    family: string;
    messages: number;
    pct: number;
};

export type UsageBlock = {
    total_messages: Metric;
    active_users: Metric;
    messages_per_user: Metric;
    top_decile_threshold: Metric;
    top_decile_avg: Metric;
};

export type SlideStudioBlock = {
    presentations_created: number;
    slides_generated: number;
    presentations_exported: number;
    themes_created: LabeledCount[];
    library_pptx_imported: number;
};

export type DocStudioBlock = {
    documents_created: number;
    documents_exported: number;
};

export type CustomAgentsBlock = {
    created: LabeledCount[];
    used: number;
};

export type DocumentsBlock = {
    data_sources_created: LabeledCount[];
    documents_indexed: number;
    pages_indexed: number;
    attached_files: number;
};

export type ProjectsBlock = {
    projects_created: number;
    messages_per_project: number;
};

export type ToolsBlock = {
    /** Runs captured over the window — the denominator of every `pct` below, and not the
     *  same number as `usage.total_messages`: capture starts at deployment. */
    total_messages: number;
    families: ToolFamilyUsage[];
};

export type AnalyticsDashboard = {
    period: AnalyticsPeriod;
    usage: UsageBlock;
    slide_studio: SlideStudioBlock;
    doc_studio: DocStudioBlock;
    custom_agents: CustomAgentsBlock;
    documents: DocumentsBlock;
    projects: ProjectsBlock;
    models: ModelUsageCount[];
    tools: ToolsBlock;
};

// The backend scopes the result: a tenant admin gets their own tenant, an Elqano admin every
// tenant cumulated.
export async function getAnalytics(
    tenantId: string,
    period: AnalyticsPeriod,
    signal?: AbortSignal
): Promise<AnalyticsDashboard> {
    const params = new URLSearchParams({ tenant_id: tenantId, period });
    const response = await apiClient.get(`/analytics?${params.toString()}`, {
        signal
    });
    return await response.json();
}
